"use client";

import React, { useEffect, useState } from "react";
import { Folder, Tag } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";

function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          <div className="aspect-[4/3] bg-muted rounded-lg"></div>
          <div className="space-y-6">
            <div className="h-10 bg-muted rounded w-3/4"></div>
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded w-full"></div>
              <div className="h-4 bg-muted rounded w-5/6"></div>
            </div>
            <div className="h-12 bg-muted rounded w-full"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 text-center py-12">
        <p className="text-destructive font-default">Failed to load floral details.</p>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="container mx-auto px-4 text-center py-12">
        <p className="text-muted-foreground font-default">Floral arrangement not found.</p>
      </div>
    );
  }

  const product = products[0];

  return (
    <div className="container mx-auto px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
        {}
        <div className="flex flex-col gap-4">
          {product.featured_image_url ? (
            <div className="aspect-[4/3] overflow-hidden rounded-lg bg-muted border border-border shadow-md">
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          ) : (
            <div className="aspect-[4/3] flex items-center justify-center rounded-lg bg-muted border border-border">
              <span className="text-muted-foreground font-default">No floral image available</span>
            </div>
          )}
        </div>

        {}
        <div className="flex flex-col">
          <h1 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4 tracking-tight">
            {product.title}
          </h1>

          <div className="flex items-baseline gap-3 mb-6">
            {product.discounted_price ? (
              <>
                <span className="text-2xl md:text-3xl font-semibold text-primary font-default">
                  ${product.discounted_price.toFixed(2)}
                </span>
                <span className="text-lg text-muted-foreground line-through font-default">
                  ${product.price.toFixed(2)}
                </span>
              </>
            ) : (
              <span className="text-2xl md:text-3xl font-semibold text-primary font-default">
                ${product.price?.toFixed(2) || "0.00"}
              </span>
            )}
          </div>

          {product.short_description && (
            <div
              className="prose prose-sm md:prose-base text-muted-foreground mb-8 font-default [&_p]:mb-4 last:[&_p]:mb-0"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          <div className="mb-8">
            <AddToCartButton
              productId={product.id}
              className="w-full"
              buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90 w-full h-12 text-lg font-default rounded-lg shadow-sm transition-all"
              text="Add to Bouquet"
              showQuantity={true}
            />
          </div>

          <Separator className="my-6 bg-border" />

          {}
          <div className="space-y-3 text-sm font-default">
            {product.categories && product.categories.length > 0 && (
              <div className="flex items-start gap-2">
                <Folder className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="flex flex-wrap gap-1">
                  <span className="text-muted-foreground font-medium mr-1">Garden Collections:</span>
                  {product.categories.map((cat, i) => (
                    <span key={cat.id} data-index={i} className="inline-flex items-center">
                      <Link
                        to={cat.link}
                        className="text-foreground hover:text-primary transition-colors underline decoration-primary/30 underline-offset-4"
                      >
                        {cat.name}
                      </Link>
                      {i < product.categories.length - 1 && (
                        <span className="text-muted-foreground ml-1">,</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="flex items-start gap-2">
                <Tag className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <div className="flex flex-wrap gap-1">
                  <span className="text-muted-foreground font-medium mr-1">Floral Tags:</span>
                  {product.tags.map((tag, i) => (
                    <span key={tag.id} data-index={i} className="inline-flex items-center">
                      <Link
                        to={tag.link}
                        className="text-foreground hover:text-primary transition-colors"
                      >
                        {tag.name}
                      </Link>
                        {i < product.tags.length - 1 && (
                        <span className="text-muted-foreground ml-1">,</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {}
      {product.long_description && (
        <div className="mt-16 pt-12 border-t border-border">
          <h2 className="text-2xl font-bold font-default text-foreground mb-6 tracking-tight">
            Garden Notes & Care
          </h2>
          <div
            className="prose max-w-none text-muted-foreground font-default [&>h1]:font-default [&>h2]:font-default [&>h3]:font-default [&>h4]:font-default [&>h5]:font-default [&>h6]:font-default"
            dangerouslySetInnerHTML={{ __html: product.long_description }}
          />
        </div>
      )}
    </div>
  );
}

export default function SingleProductTemplateSection() {
  const [queryParams, setQueryParams] = useState<any>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && (window as any).wvcClient) {
      setQueryParams((window as any).wvcClient.getCurrentWPQueryParams());
    }
  }, []);

  return (
    <section data-section-id="252299" id="single-product-template-section" className="bg-background text-foreground py-12 md:py-24">
      {queryParams ? (
        <EcommerceProductsProvider wp_query={queryParams}>
          <SingleProductConsumer />
        </EcommerceProductsProvider>
      ) : (
        <div className="container mx-auto px-4 py-24 flex justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-lg animate-spin"></div>
        </div>
      )}
    </section>
  );
}